/* eslint-disable no-console */
// Resiliência dos softphones SIP (AciSipClient e WhatsAppSipClient) contra o
// ciclo de vida da aba: o registro no Asterisk depende de timers de página
// (keep-alive CRLF + re-REGISTER), e o Chrome/Edge throttla timers de aba em
// background (1/min; após 5min, 1/10min em timers encadeados) e congela tudo
// no sleep — o contato expira no Asterisk e o telefone morre em silêncio.

/**
 * Tick periódico num Web Worker dedicado — timers de worker NÃO sofrem o
 * throttling de aba em background, então o keep-alive continua a cada 25s
 * mesmo com a aba atrás. Worker indisponível (ex.: CSP bloqueando blob:)
 * degrada pra setInterval com aviso explícito no console.
 *
 * @param {number} intervalMs
 * @param {Function} onTick
 * @returns {{ stop: Function }}
 */
export function createTickWorker(intervalMs, onTick) {
  try {
    const source = `setInterval(function () { postMessage('tick'); }, ${Number(intervalMs)});`;
    const blob = new Blob([source], { type: 'application/javascript' });
    const url = URL.createObjectURL(blob);
    const worker = new Worker(url);
    URL.revokeObjectURL(url);
    worker.onmessage = () => onTick();
    return {
      stop() {
        worker.terminate();
      },
    };
  } catch (error) {
    console.warn(
      '[sipResilience] Web Worker indisponível — keep-alive via setInterval, sujeito a throttling em background:',
      error.message
    );
    const intervalId = setInterval(onTick, intervalMs);
    return {
      stop() {
        clearInterval(intervalId);
      },
    };
  }
}

/**
 * Dispara `onWake(reason)` quando a aba/máquina/rede "volta": rede reconectou
 * (online), aba ganhou foco ou ficou visível, ou a página saiu do bfcache
 * (pageshow). É o que fura o backoff da reconexão — sem isso, um laptop que
 * dormiu acorda com o timer de reconexão congelado e o telefone morto.
 *
 * @param {Function} onWake — recebe o nome do evento que acordou
 * @returns {Function} unbind
 */
export function bindWakeEvents(onWake) {
  const onOnline = () => onWake('online');
  const onFocus = () => onWake('focus');
  const onPageShow = () => onWake('pageshow');
  const onVisibility = () => {
    if (document.visibilityState === 'visible') onWake('visibilitychange');
  };

  window.addEventListener('online', onOnline);
  window.addEventListener('focus', onFocus);
  window.addEventListener('pageshow', onPageShow);
  document.addEventListener('visibilitychange', onVisibility);

  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('focus', onFocus);
    window.removeEventListener('pageshow', onPageShow);
    document.removeEventListener('visibilitychange', onVisibility);
  };
}
